import React from 'react';

import inputStyles from './personalisation-inputs.module.css';
import gridStyles from './personalisation-grid.module.css';

export interface OptionSelectionItem {
  id: string;
  /** Card title. A node rather than a string so a label can carry a struck-through price. */
  label: React.ReactNode;
  /** Optional supporting line under the title. */
  description?: string;
}

interface OptionSelectionProps {
  /** Radio group name attribute. */
  name: string;
  /** Fieldset legend (e.g. "Choose option"). */
  legend: string;
  options: OptionSelectionItem[];
  /** Currently selected option id (groupName). */
  selected: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Grid column count (default 3). */
  columns?: number;
}

/** Generic option selector (e.g. colour / foil style) mirroring font selection layout. */
export function OptionSelection({
  name,
  legend,
  options,
  selected,
  onChange,
  disabled = false,
  columns = 3
}: OptionSelectionProps): React.JSX.Element | null {
  if (!Array.isArray(options) || options.length === 0) {
    return null;
  }

  const columnClass =
    columns <= 2
      ? gridStyles['personalisation__selection-grid--2-column']
      : gridStyles[`personalisation__selection-grid--${Math.min(columns, 4)}-column`] ||
        gridStyles['personalisation__selection-grid--3-column'];

  return (
    <fieldset className={inputStyles['personalisation__input-option-selection']}>
      {options.length > 1 && (
        <legend
          className={inputStyles['personalisation__input-font-selection-label'] + ' body-xs body-uppercase font-medium'}
        >
          {legend}
        </legend>
      )}
      <ul className={gridStyles['personalisation__selection-grid'] + ' ' + columnClass} role="list">
        {options.map((option) => (
          <li key={option.id} className={inputStyles['personalisation__input-font-selection-card']}>
            <input
              disabled={disabled}
              type="radio"
              id={`${name}-${option.id}`}
              name={name}
              value={option.id}
              checked={selected === option.id}
              onChange={() => onChange(option.id)}
              className={inputStyles['personalisation__input-font-selection-hidden-input']}
            />
            <label
              htmlFor={`${name}-${option.id}`}
              className={
                inputStyles['personalisation__input-font-selection-option-label'] +
                ' ' +
                inputStyles['personalisation__input-option-selection-option-label']
              }
            >
              <p className={inputStyles['personalisation__input-option-selection-label'] + ' body-sm font-medium'}>
                {option.label}
              </p>
              {option.description && (
                <p className={inputStyles['personalisation__input-option-selection-description'] + ' body-xs'}>
                  {option.description}
                </p>
              )}
            </label>
          </li>
        ))}
      </ul>
    </fieldset>
  );
}
